import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { listCompanies, getCompany, getSettings, ApiError } from './api.js';
import type { CompanyCard, CompanyDetail, Settings } from './api.js';
import { fixtureCompanies, fixtureDetail, fixtureSettings } from './fixtures.js';

// One place decides whether the app is running on live data or fixtures, so the
// "live" badge can never be true while half the screen is mock. Every view reads
// this instead of fetching for itself.

interface DataState {
  companies: CompanyCard[] | null;
  settings: Settings | null;
  live: boolean;
  error: string | null;
  /** True when the API answered 401 — the secret is wrong or absent. */
  needsSecret: boolean;
  /** Re-run the initial load, e.g. after entering the API secret. */
  retry(): void;
  /** Detail loader — falls back to fixtures the same way the list does. */
  loadCompany(slug: string): Promise<CompanyDetail | null>;
}

const Ctx = createContext<DataState | null>(null);

export function DataProvider({ children }: { children: ReactNode }) {
  const [companies, setCompanies] = useState<CompanyCard[] | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [live, setLive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsSecret, setNeedsSecret] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listCompanies(), getSettings()])
      .then(([c, s]) => {
        if (cancelled) return;
        setCompanies(c.companies);
        setSettings(s.settings);
        setLive(true);
        setNeedsSecret(false);
        setError(null);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        // 401 means the API is up and the secret is missing or wrong — that's
        // fixable by the user, so surface it instead of silently showing
        // fixtures. Anything else (no database, function down) falls back.
        if (err.status === 401) {
          setNeedsSecret(true);
          setError(attempt > 0 ? 'That secret was not accepted.' : null);
          return;
        }
        // THE SILENT SWAP THAT ATE THE BUTTONS. One transient failure on
        // either boot call used to put the whole app on demo fixtures with
        // only a tiny "sample data" badge to say so — Enrich vanished, records
        // served mannequin data, and every gated control "stopped firing".
        // request() now retries reads, so reaching here means the API is
        // genuinely down; in production that is said OUT LOUD with a retry,
        // and fixtures remain only as the dev-mode preview they were built as.
        if (import.meta.env.DEV) {
          console.info('API unavailable (%s) — DEV fixture fallback', err.message);
          setCompanies(fixtureCompanies);
          setSettings(fixtureSettings);
          setLive(false);
          setNeedsSecret(false);
          setError(null);
          return;
        }
        setError(`The server could not be reached (${err.message}). Nothing you see can be trusted until this clears.`);
      });
    return () => { cancelled = true; };
  }, [attempt]);

  const value: DataState = {
    companies,
    settings,
    live,
    error,
    needsSecret,
    retry: () => setAttempt((n) => n + 1),
    async loadCompany(slug) {
      if (!live) return fixtureDetail(slug);
      try {
        return (await getCompany(slug)).company;
      } catch {
        return fixtureDetail(slug);
      }
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useData(): DataState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useData must be used inside DataProvider');
  return v;
}

/** Stage keys in pipeline order. Mirrors companies_stage_ck in db/schema.sql. */
export const STAGES = [
  { key: 'identified', label: 'Identified' },
  { key: 'qualified', label: 'Qualified' },
  { key: 'warming', label: 'Warming' },
  { key: 'engaged', label: 'Engaged' },
  { key: 'meeting_set', label: 'Meeting set' },
  { key: 'opportunity', label: 'Opportunity' },
  { key: 'won', label: 'Won' },
  { key: 'nurture', label: 'Nurture' },
  { key: 'watch', label: 'Watch' },
  // The DB has always allowed 'lost'; leaving it off this list rendered a
  // lost account's stage select BLANK, and the only way to mark a loss was
  // the API. A pipeline that cannot record losing flatters itself.
  { key: 'lost', label: 'Lost' },
] as const;

export const stageLabel = (key: string) =>
  STAGES.find((s) => s.key === key)?.label ?? key.replace('_', ' ');

/**
 * What is actually stopping this account from moving. Ordered by severity so the
 * first entry is the one worth acting on — the dashboard and the cards both read
 * from this, so "what blocks it" is defined once.
 */
export function blockers(c: CompanyCard): { label: string; hard: boolean }[] {
  const out: { label: string; hard: boolean }[] = [];
  if (c.conflict_gate === 'conflict') out.push({ label: 'client conflict', hard: true });
  else if (c.conflict_gate !== 'clear' && c.touches_total > 0)
    out.push({ label: 'conflict gate not run', hard: true });
  if (c.contact_count > 0 && c.contacts_with_email === 0)
    out.push({ label: 'no coordinates', hard: true });
  if (c.touches_total > 0 && c.touches_written < c.touches_total)
    out.push({ label: `${c.touches_total - c.touches_written} touches unwritten`, hard: false });
  if (c.open_question_count > 0)
    out.push({ label: `${c.open_question_count} open`, hard: false });
  return out;
}
